import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api, type Deck } from '../api'
import { DeckImage } from '../components/DeckImage'
import { ProblemBanner } from '../components/Problem'

/**
 * Self-paced card browser. Keyboard-first: space flips, arrows navigate (spec §8.2).
 *
 * Learn mode reads the deck directly rather than driving a session: there is no grading
 * to withhold, and a browse should not need a round trip per card.
 */
export default function Learn() {
  const { deckId = '' } = useParams()
  const [deck, setDeck] = useState<Deck | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [index, setIndex] = useState(0)
  const [flipped, setFlipped] = useState(false)

  useEffect(() => {
    api.getDeck(deckId).then(setDeck).catch(setError)
    // Starting a session is what refreshes the deck's staleness clock; a browse does
    // the same, because either is genuine use.
    api.startSession({ deckId, mode: 'learn', portion: 'all', shuffle: false }).catch(() => {})
  }, [deckId])

  const total = deck?.cards.length ?? 0
  const move = useCallback(
    (delta: number) => {
      setIndex((current) => Math.min(Math.max(current + delta, 0), Math.max(total - 1, 0)))
      setFlipped(false)
    },
    [total],
  )

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === ' ') {
        event.preventDefault()
        setFlipped((value) => !value)
      } else if (event.key === 'ArrowRight') {
        move(1)
      } else if (event.key === 'ArrowLeft') {
        move(-1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [move])

  if (error) return <ProblemBanner error={error} />
  if (!deck) return <p className="muted">Loading…</p>

  const card = deck.cards[index]
  if (!card) return <p className="muted">This deck has no cards.</p>

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1>{deck.title}</h1>
        <Link to={`/decks/${deck.deckId}`}>Back to deck</Link>
      </div>

      <div className="progress" aria-hidden="true">
        <span style={{ width: `${((index + 1) / total) * 100}%` }} />
      </div>
      <p className="muted">
        Card {index + 1} of {total}
      </p>

      <article
        className="card flashcard"
        onClick={() => setFlipped((value) => !value)}
        aria-live="polite"
      >
        <p>{flipped ? card.back : card.front}</p>
        {!flipped && card.hint ? <p className="muted">Hint: {card.hint}</p> : null}
        {card.image ? <DeckImage name={card.image} /> : null}
      </article>

      <div className="row">
        <button onClick={() => move(-1)} disabled={index === 0}>
          Previous
        </button>
        <button className="primary" onClick={() => setFlipped((value) => !value)}>
          {flipped ? 'Show question' : 'Show answer'}
        </button>
        <button onClick={() => move(1)} disabled={index >= total - 1}>
          Next
        </button>
      </div>
      <p className="keyhint">Space flips the card. Left and right arrows move between cards.</p>
    </>
  )
}
